<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { applyCommunityBranding, clearCommunityBranding } from '@/branding/runtime';
import HomepageItemPrivateNoAccessDialog from '@/components/dialogs/HomepageItemPrivateNoAccessDialog.vue';
import PageLoading from '@/views/PageLoading.vue';
import { usePage } from '@/composables/usePage';
import { type HomepageItemNoAccessPayload } from '@/composables/useHomepageItemNavigation';
import { getTranslation } from '@/utils/data-display-utils';
import { useCommunityStore } from '@/store/communityStore';
import { useConfigurationStore } from '@/store/configurationStore';
import { useUserStore } from '@/store/userStore';
import type { HomepageItem } from '@/store/homepageItem.interface';
import { themeRegistry } from '@/themes/registry';

const props = withDefaults(
  defineProps<{
    pageId?: number | null;
    slug?: string | null;
    isHomepage?: boolean;
  }>(),
  {
    pageId: null,
    slug: null,
    isHomepage: false,
  },
);

const route = useRoute();
const router = useRouter();
const communityStore = useCommunityStore();
const configurationStore = useConfigurationStore();
const userStore = useUserStore();

const resolvedPageId = computed(() => props.pageId ?? null);
const resolvedSlug = computed(() => props.slug ?? (route.params.slug as string | undefined) ?? null);

const { page, blocks, themeKey, isLocaleSupported, isLoading, currentLocale, loadPage } = usePage(
  resolvedPageId,
  resolvedSlug,
);

const layoutComponent = computed(() => defineAsyncComponent(themeRegistry.getLayout('page', themeKey.value)));
const designSystem = computed(() => themeRegistry.getDesignSystem('page', themeKey.value));
const homepageItemPrivateNoAccessDialogRef = ref<InstanceType<typeof HomepageItemPrivateNoAccessDialog>>();
const selectedItemNeedAuth = ref<HomepageItem | null>(null);
const needLogin = ref(false);

function openNoAccessDialog(item: HomepageItem, loginRequired = false) {
  selectedItemNeedAuth.value = item;
  needLogin.value = loginRequired;
  homepageItemPrivateNoAccessDialogRef.value?.openDialog();
}

function handleNoAccess(payload: HomepageItemNoAccessPayload) {
  openNoAccessDialog(payload.item, payload.needLogin);
}

watch(
  [resolvedPageId, resolvedSlug],
  async () => {
    await loadPage();
  },
  { immediate: true },
);

watch(
  [() => isLoading.value, () => page.value],
  async ([loading, currentPage]) => {
    if (loading) return;

    if (!currentPage) {
      await router.replace({ name: 'not-found' });
      return;
    }

    document.title =
      getTranslation(communityStore.community, 'title', communityStore.community?.languageEnabled) ?? document.title;
  },
  { immediate: true },
);

watch(
  [() => isLoading.value, () => route.query.projectId, () => communityStore.isLoaded],
  ([loading, projectId, communityLoaded]) => {
    if (loading || !projectId || !communityLoaded) return;

    const parsedProjectId = parseInt(projectId as string, 10);
    if (Number.isNaN(parsedProjectId)) return;

    const project = communityStore.getProject(parsedProjectId);
    if (!project) return;

    const result = communityStore.isUserAllowedAccessProject(project, userStore.user);
    if (!result.allowed) {
      openNoAccessDialog(project, result.reason === 'login');
    }
  },
  { immediate: true },
);

watch(
  [() => designSystem.value, () => communityStore.community, () => configurationStore.brandingPresets],
  async ([currentDesignSystem, currentCommunity]) => {
    if (currentDesignSystem !== 'modern') {
      clearCommunityBranding();
      return;
    }

    await applyCommunityBranding(currentCommunity);
  },
  { immediate: true, deep: true },
);

onBeforeUnmount(() => {
  clearCommunityBranding();
});
</script>

<template>
  <div v-if="!isLoading" class="page-view" :class="`page-view--${designSystem}`" :data-design-system="designSystem">
    <component
      v-if="page"
      :is="layoutComponent"
      :page="page"
      :blocks="blocks"
      :community="communityStore.community"
      :theme-key="themeKey"
      :is-locale-supported="isLocaleSupported"
      :current-locale="currentLocale"
      @no-access="handleNoAccess"
    />
    <HomepageItemPrivateNoAccessDialog
      v-if="page"
      ref="homepageItemPrivateNoAccessDialogRef"
      :home-link="!props.isHomepage"
      :item="selectedItemNeedAuth"
      :need-login="needLogin"
      :language-enabled="communityStore.community?.languageEnabled"
    />
  </div>
  <PageLoading v-else :language-enabled="communityStore.community?.languageEnabled" />
</template>

<style scoped>
.page-view {
  display: flex;
  flex: 1 1 auto;
  min-height: 100%;
  flex-direction: column;
}
</style>
